import { useMemo, useEffect, useRef } from 'react'
import * as THREE from 'three'
import { useFrame } from '@react-three/fiber'
import { MeshBasicNodeMaterial } from 'three/webgpu'
import {
  Fn, uniform, vec2, vec3, float,
  fract, sin, dot, floor, abs, max, mix, clamp,
  time, positionLocal
} from 'three/tsl'
import useGameStore from '../../stores/gameStore'
import { moodState } from '../../lib/world/mood'

// ──────────────────────────────────────────────────────────────────
// Day/night colour palette. dayPhase is 0..1 with 0=midnight, 0.25=dawn,
// 0.5=noon, 0.75=dusk. Each entry holds the target sky-top + sky-bottom
// colours; the actual shader uniforms lerp toward these every frame.
// ──────────────────────────────────────────────────────────────────
const SKY_PALETTE = [
  // [phase, top hex, bottom hex, cloudDark, cloudLight]
  // Stops track the nightness ramps in lib/world/mood.js — if you move one,
  // move the other, or the sky and the lighting disagree about the hour.
  [0.00, '#0a0e2a', '#1a1336', 0.18, 0.10], // deep night (short now)
  [0.10, '#3b2d52', '#7a4f6e', 0.28, 0.18], // pre-dawn
  [0.20, '#ff8a6a', '#ffd1a8', 0.40, 0.30], // dawn — warm
  [0.34, '#3aa7c4', '#b9c6ff', 0.44, 0.30], // morning
  [0.50, '#0096b0', '#899dfc', 0.46, 0.28], // midday — original palette
  [0.68, '#5fb8d4', '#ffc28a', 0.42, 0.26], // afternoon → golden
  [0.82, '#ff6a4a', '#ff9a76', 0.38, 0.22], // dusk
  [0.93, '#1a1648', '#2e2358', 0.22, 0.12], // night onset
  [1.00, '#0a0e2a', '#1a1336', 0.18, 0.10], // wrap to midnight
]

function _lerpColor(out, aHex, bHex, t) {
  const ca = new THREE.Color(aHex)
  const cb = new THREE.Color(bHex)
  out.copy(ca).lerp(cb, t)
}

function paletteFor(phase) {
  const p = ((phase % 1) + 1) % 1
  for (let i = 0; i < SKY_PALETTE.length - 1; i++) {
    const [p0] = SKY_PALETTE[i]
    const [p1] = SKY_PALETTE[i + 1]
    if (p >= p0 && p <= p1) {
      const t = (p - p0) / Math.max(0.0001, p1 - p0)
      return { i, j: i + 1, t }
    }
  }
  return { i: 0, j: 1, t: 0 }
}

// TSL hash function for 2D -> 2D
const hash = Fn(([p_immutable]) => {
  const p = vec2(p_immutable).toVar()
  const px = dot(p, vec2(127.1, 311.7))
  const py = dot(p, vec2(269.5, 183.3))
  return fract(sin(vec2(px, py)).mul(43758.5453123)).mul(2.0).sub(1.0)
})

// Simplex-like noise
const noise = Fn(([p_immutable]) => {
  const p = vec2(p_immutable).toVar()
  const K1 = float(0.366025404)
  const K2 = float(0.211324865)

  const i = floor(p.add(p.x.add(p.y).mul(K1)))
  const a = p.sub(i).add(i.x.add(i.y).mul(K2))

  const ox = a.x.greaterThan(a.y).select(float(1.0), float(0.0))
  const oy = a.x.greaterThan(a.y).select(float(0.0), float(1.0))
  const o = vec2(ox, oy)

  const b = a.sub(o).add(K2)
  const c = a.sub(1.0).add(K2.mul(2.0))

  const h = max(float(0.5).sub(vec3(dot(a, a), dot(b, b), dot(c, c))), 0.0)
  const n = h.mul(h).mul(h).mul(h).mul(vec3(
    dot(a, hash(i)),
    dot(b, hash(i.add(o))),
    dot(c, hash(i.add(1.0)))
  ))

  return dot(n, vec3(70.0))
})

// FBM with matrix rotation between octaves
const fbm = Fn(([n_immutable]) => {
  const n = vec2(n_immutable).toVar()
  const total = float(0.0).toVar()
  const amplitude = float(0.1).toVar()

  // 3 octaves with matrix rotation m = mat2(1.6, 1.2, -1.2, 1.6)
  total.addAssign(noise(n).mul(amplitude))
  n.assign(vec2(n.x.mul(1.6).add(n.y.mul(1.2)), n.x.mul(-1.2).add(n.y.mul(1.6))))
  amplitude.mulAssign(0.4)

  total.addAssign(noise(n).mul(amplitude))
  n.assign(vec2(n.x.mul(1.6).add(n.y.mul(1.2)), n.x.mul(-1.2).add(n.y.mul(1.6))))
  amplitude.mulAssign(0.4)

  total.addAssign(noise(n).mul(amplitude))

  return total
})

// Helper to apply the rotation matrix m = mat2(1.6, 1.2, -1.2, 1.6) to a vec2
const applyM = (v) => vec2(v.x.mul(1.6).add(v.y.mul(1.2)), v.x.mul(-1.2).add(v.y.mul(1.6)))

function Moon() {
  const material = useMemo(() => new MeshBasicNodeMaterial({ color: new THREE.Color('#FF4AC8') }), [])
  useEffect(() => {
    return () => material.dispose()
  }, [material])

  return (
    <mesh position={[-10, 80, -250]}>
      <sphereGeometry args={[30, 16, 16]} />
      <primitive object={material} attach="material" />
    </mesh>
  )
}

export function Sky({ onUniforms }) {
  const uniformsRef = useRef(null)

  const { material, skyUniforms } = useMemo(() => {
    const mat = new MeshBasicNodeMaterial()
    mat.side = THREE.BackSide

    const resolution = uniform(new THREE.Vector2(3840, 2160))
    const cloudscale = uniform(1.7)
    const speed = uniform(0.03)
    const clouddark = uniform(0.46)
    const cloudlight = uniform(0.28)
    const cloudcover = uniform(0.01)
    const cloudalpha = uniform(0.0)
    const skytint = uniform(0.61)
    const skycolour1 = uniform(new THREE.Color('#0096b0'))
    const skycolour2 = uniform(new THREE.Color('#899dfc'))

    const skyUniforms = { cloudscale, speed, clouddark, cloudlight, cloudcover, cloudalpha, skytint, skycolour1, skycolour2 }

    mat.colorNode = Fn(() => {
      // Stereographic projection from bottom pole — seamless, no UV seam
      const dir = positionLocal.normalize()
      const gradientY = dir.y.mul(0.5).add(0.5)
      const noiseUV = dir.xz.div(dir.y.add(1.0001))
      const uvScaled = noiseUV.mul(vec2(resolution.x.div(resolution.y), 1.0))
      const t = time.mul(speed)
      const q = fbm(uvScaled.mul(cloudscale).mul(0.5))

      // First noise layer (r) - 3 iterations
      const r = float(0.0).toVar()
      const uv1 = uvScaled.mul(cloudscale).sub(vec2(q).sub(t)).toVar()
      const w1 = float(0.8).toVar()

      r.addAssign(abs(w1.mul(noise(uv1))))
      uv1.assign(applyM(uv1).add(t))
      w1.mulAssign(0.7)
      r.addAssign(abs(w1.mul(noise(uv1))))
      uv1.assign(applyM(uv1).add(t))
      w1.mulAssign(0.7)
      r.addAssign(abs(w1.mul(noise(uv1))))

      // Second noise layer (f) - 3 iterations
      const f = float(0.0).toVar()
      const uv2 = uvScaled.mul(cloudscale).sub(vec2(q).sub(t)).toVar()
      const w2 = float(0.7).toVar()

      f.addAssign(w2.mul(noise(uv2)))
      uv2.assign(applyM(uv2).add(t))
      w2.mulAssign(0.6)
      f.addAssign(w2.mul(noise(uv2)))
      uv2.assign(applyM(uv2).add(t))
      w2.mulAssign(0.6)
      f.addAssign(w2.mul(noise(uv2)))

      f.mulAssign(r.add(f))

      // Third noise layer (c) - 3 iterations
      const c = float(0.0).toVar()
      const t2 = time.mul(speed).mul(2.0)
      const uv3 = uvScaled.mul(cloudscale).mul(2.0).sub(vec2(q).sub(t2)).toVar()
      const w3 = float(0.4).toVar()

      c.addAssign(w3.mul(noise(uv3)))
      uv3.assign(applyM(uv3).add(t2))
      w3.mulAssign(0.6)
      c.addAssign(w3.mul(noise(uv3)))
      uv3.assign(applyM(uv3).add(t2))
      w3.mulAssign(0.6)
      c.addAssign(w3.mul(noise(uv3)))

      // Fourth noise layer (c1) - 3 iterations
      const c1 = float(0.0).toVar()
      const t3 = time.mul(speed).mul(3.0)
      const uv4 = uvScaled.mul(cloudscale).mul(3.0).sub(vec2(q).sub(t3)).toVar()
      const w4 = float(0.4).toVar()

      c1.addAssign(abs(w4.mul(noise(uv4))))
      uv4.assign(applyM(uv4).add(t3))
      w4.mulAssign(0.6)
      c1.addAssign(abs(w4.mul(noise(uv4))))
      uv4.assign(applyM(uv4).add(t3))
      w4.mulAssign(0.6)
      c1.addAssign(abs(w4.mul(noise(uv4))))

      c.addAssign(c1)

      // Sky gradient + cloud color
      const skycolour = mix(vec3(skycolour2), vec3(skycolour1), gradientY)
      const cloudcolour = vec3(0.722, 0.463, 0.471).mul(clamp(clouddark.add(cloudlight.mul(c)), 0.0, 1.0))

      const fFinal = cloudcover.add(cloudalpha.mul(f).mul(r))
      return mix(skycolour, clamp(skycolour.mul(skytint).add(cloudcolour), 0.0, 1.0), clamp(fFinal.add(c), 0.0, 1.0))
    })()

    return { material: mat, skyUniforms }
  }, [])

  useEffect(() => {
    if (onUniforms) onUniforms(skyUniforms)
  }, [skyUniforms, onUniforms])

  useEffect(() => {
    return () => material.dispose()
  }, [material])

  // Day/night drive — lerp sky colours toward the palette target each frame.
  // Reads dayPhase from the gameStore (populated by useSimConnection from
  // periodic world_delta broadcasts). When sim-server is offline we simply
  // hold the last value, so the editor view doesn't go dark unexpectedly.
  const tmpTop = useRef(new THREE.Color()).current
  const tmpBot = useRef(new THREE.Color()).current
  // Scratch for the HSL round trip, so the damping allocates nothing per frame.
  const tmpHsl = useRef({ h: 0, s: 0, l: 0 }).current
  // True until the first frame that saw a real dayPhase; see below.
  const firstPhase = useRef(true)
  useFrame((_, dt) => {
    const env = useGameStore.getState().environment
    // The sun's phase, which stops with the sun when the town clock has
    // been let go far enough. Falls back to the hour so an older sim, or
    // the frames before the first environment lands, draw a normal sky.
    const phase = env?.sunPhase ?? env?.dayPhase
    if (phase == null) return
    const { i, j, t } = paletteFor(phase)
    const a = SKY_PALETTE[i], b = SKY_PALETTE[j]
    _lerpColor(tmpTop, a[1], b[1], t)
    _lerpColor(tmpBot, a[2], b[2], t)
    const cloudDarkTarget = a[3] * (1 - t) + b[3] * t
    const cloudLightTarget = a[4] * (1 - t) + b[4] * t
    // Smoothing factor — slow enough that fast scrubs don't strobe, fast
    // enough that the cycle is visible during a 5-min day.
    //
    // First frame with a real phase SNAPS instead. The shader boots on the
    // day palette and the first world_delta arrives seconds later, so a
    // visitor landing at night watched the sky visibly catch up, dimming
    // in front of them like the world was late. Sam clocked it. Snap once,
    // lerp forever after; the veil holds until this has happened.
    const alpha = firstPhase.current ? 1 : Math.min(1, dt * 0.6)
    if (firstPhase.current) {
      firstPhase.current = false
      useGameStore.getState().setSkySettled?.(true)
    }
    skyUniforms.clouddark.value += (cloudDarkTarget - skyUniforms.clouddark.value) * alpha
    skyUniforms.cloudlight.value += (cloudLightTarget - skyUniforms.cloudlight.value) * alpha

    // WEATHER = MOOD: overcast lids the sky with cloud, storms churn it
    // faster. moodState is smoothed by MoodRig from the live environment
    // (danger / vitality / warmth / abundance), so a governance shift
    // reads as weather rolling in.
    const oc = moodState.overcast

    // The sky COLOUR has to move too, and it did not.
    //
    // Sam: "if it's raining in the day, I can see blue sky". He was right,
    // and the cause is that the two halves were driven by different
    // things: colour came only from the day/night palette, while overcast
    // only added cloud on top. So a downpour at noon still painted that
    // bright #0096b0 behind the rain.
    //
    // Desaturating and darkening the palette colour, rather than blending
    // toward a fixed grey, keeps it correct at every hour: an overcast
    // dawn stays a dishwater pink and an overcast midnight just gets
    // flatter, instead of both being pushed to the same daytime slate.
    // This is also the thing that makes the token's pulse legible, since
    // it moves vitality, which is most of what moves wetness.
    if (oc > 0.02) {
      const damp = (c) => {
        c.getHSL(tmpHsl)
        c.setHSL(
          tmpHsl.h,
          tmpHsl.s * (1 - oc * 0.8),      // colour drains out of it
          tmpHsl.l * (1 - oc * 0.42),     // and the light goes with it
        )
      }
      damp(tmpTop)
      damp(tmpBot)
    }

    skyUniforms.skycolour1.value.lerp(tmpTop, alpha)
    skyUniforms.skycolour2.value.lerp(tmpBot, alpha)

    // Properly lidded at full overcast rather than lightly dusted: 0.32
    // cover left gaps of clear sky visible through the worst of it.
    skyUniforms.cloudcover.value += (0.01 + oc * 0.6 - skyUniforms.cloudcover.value) * alpha
    skyUniforms.cloudalpha.value += (oc * 8.0 - skyUniforms.cloudalpha.value) * alpha
    skyUniforms.speed.value += (0.03 + moodState.danger * 0.08 - skyUniforms.speed.value) * alpha
  })

  return (
    <>
      <mesh position={[0, 0, 0]}>
        <sphereGeometry args={[300, 16, 16]} />
        <primitive object={material} attach="material" />
      </mesh>
      <Moon />

    </>
  )
}
